import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ApiError, api } from '../api';
import { GoogleLogo, MicrosoftLogo, TalogentFooterBar } from '@platform/ui-kit';

/**
 * Sign-in screen.
 *
 * Local bcrypt accounts, plus Microsoft Entra ID / Google Workspace SSO when this
 * deployment has them enabled. The server distinguishes a locked or deactivated
 * account from bad credentials, because a user who cannot get in needs to know
 * whether to try again or call an administrator — but a wrong username and a wrong
 * password both return the same thing, so this form cannot be used to discover
 * which addresses exist.
 *
 * SSO structure settled via a prototype (Wayfinder ticket "Prototype the login
 * screen UI/UX", github.com/gltalogent/portfolio-platform/issues/7): buttons up
 * top, local password sign-in available behind a link rather than always shown —
 * the two other variants explored (tabs; identify-yourself-first) are preserved on
 * the `prototype/login-sso-ui` branch, not here.
 */
const MESSAGES: Record<string, string> = {
  invalid_credentials: 'That email address or password is not correct.',
  account_locked:
    'This account is locked after too many failed sign-in attempts. An administrator can unlock it.',
  account_inactive: 'This account has been deactivated. Contact an administrator.',
};

export interface SsoProviders {
  google: boolean;
  entra: boolean;
}

export interface LoginPageProps {
  installName?: string;
  onSignedIn: () => void;
  /**
   * Which IdPs this deployment has enabled — decides whether the SSO buttons show
   * at all. Sourced from `GET /api/session`'s `ssoProviders` field, which is
   * deliberately NOT admin-gated (unlike `GET /api/admin/sso`) since a signed-out
   * user needs it before there's a session. Defaults to neither if omitted, so an
   * unauthenticated caller that skips it still falls back to password-only.
   */
  ssoProviders?: SsoProviders;
}

/**
 * A failed SSO attempt lands back here as `/?ssoError=<message>` (see
 * `apps/portal/server/src/app.ts`'s `/api/auth/sso/callback`). Read once and strip
 * from the URL so refreshing the page doesn't keep re-showing a stale error.
 */
function readSsoErrorFromUrl(): string | undefined {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get('ssoError');
  if (!raw) return undefined;
  url.searchParams.delete('ssoError');
  window.history.replaceState(null, '', url);
  return decodeURIComponent(raw);
}

export function LoginPage({
  installName,
  onSignedIn,
  ssoProviders = { google: false, entra: false },
}: LoginPageProps) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>(readSsoErrorFromUrl);
  const [busy, setBusy] = useState(false);

  const hasSso = ssoProviders.google || ssoProviders.entra;
  const [showPassword, setShowPassword] = useState(!hasSso);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Read what is actually IN the fields, not what React thinks is there.
    //
    // A password manager fills these without always firing an input event, which
    // leaves the component state empty while the boxes visibly hold credentials.
    // Trusting state then submits blanks — or blocks the submit outright.
    const form = new FormData(event.currentTarget);
    const enteredIdentifier = String(form.get('identifier') ?? '').trim() || identifier.trim();
    const enteredPassword = String(form.get('password') ?? '') || password;

    if (!enteredIdentifier || !enteredPassword) {
      setError('Enter your email address and password.');
      return;
    }

    setBusy(true);
    setError(undefined);
    try {
      await api.login(enteredIdentifier, enteredPassword);
      onSignedIn();
    } catch (err) {
      const key = err instanceof ApiError ? err.message : '';
      setError(MESSAGES[key] ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
            <Box
              aria-hidden
              sx={{
                width: 44,
                height: 44,
                borderRadius: '10px',
                background: (t) =>
                  `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.primary.light})`,
              }}
            />
            <Typography variant="h6" component="h1" sx={{ mt: 1 }}>
              Portfolio Platform
            </Typography>
            {installName ? (
              <Typography variant="body2" color="text.secondary">
                {installName}
              </Typography>
            ) : null}
          </Stack>

          <form onSubmit={submit}>
            <Stack spacing={2.5}>
              {error ? <Alert severity="error">{error}</Alert> : null}

              {hasSso ? (
                <Stack spacing={1.25}>
                  {ssoProviders.entra ? (
                    <Button
                      component="a"
                      href="/api/auth/sso/start?provider=EntraID"
                      variant="outlined"
                      size="large"
                      sx={{ height: 44, justifyContent: 'flex-start', pl: 2 }}
                      startIcon={<MicrosoftLogo />}
                    >
                      Continue with Microsoft
                    </Button>
                  ) : null}
                  {ssoProviders.google ? (
                    <Button
                      component="a"
                      href="/api/auth/sso/start?provider=Google"
                      variant="outlined"
                      size="large"
                      sx={{ height: 44, justifyContent: 'flex-start', pl: 2 }}
                      startIcon={<GoogleLogo />}
                    >
                      Continue with Google
                    </Button>
                  ) : null}
                </Stack>
              ) : null}

              {hasSso && !showPassword ? (
                <Link component="button" type="button" variant="body2" onClick={() => setShowPassword(true)}>
                  Use a password instead
                </Link>
              ) : null}

              {hasSso && showPassword ? <Divider>or</Divider> : null}

              {showPassword ? (
                <>
                  {/* `type="text"`, not `type="email"`: the server accepts an email
                      address OR a username, and the browser's email-format check would
                      reject a perfectly valid username before the form is ever sent. */}
                  <TextField
                    label="Email or username"
                    name="identifier"
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    autoComplete="username"
                    autoFocus={!hasSso}
                    fullWidth
                  />
                  <TextField
                    label="Password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    fullWidth
                  />

                  {/* Gated only on the request being in flight. Disabling on state would
                      leave the button dead after an autofill React never saw — the field
                      looks filled and the button will not press. */}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={busy}
                    sx={{ height: 44 }}
                  >
                    {busy ? 'Signing in…' : 'Sign in'}
                  </Button>
                </>
              ) : null}
            </Stack>
          </form>
        </CardContent>
      </Card>
      {/* This screen is OUTSIDE AppShell, so the shell's footer never reaches it.
          Rendered here so the credit is present before sign-in too. */}
      <TalogentFooterBar />
    </Box>
  );
}
